import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { StockProductionCenter } from 'types/models/StockReceipt';
import { getStockProductionCenters } from 'services/stockProductionCenterServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import SelectField from 'components/SelectField';
import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import { useTranslation } from 'react-i18next';
import BooleanIcon from 'components/common/BooleanIcon';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import { mapToSelectOptions } from 'utils/select';
import Venue from 'types/models/Venue';
import UserRoleType from 'types/enums/UserRoleType';
import useSetupInitialState from 'hooks/useSetupInitialState';

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedProductionCenter } = React.useContext(EditStockModuleContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;
  const [loading, setLoading] = React.useState(true);

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [venueId, setVenueId] = React.useState<string>('');
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockProductionCenter>>();

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      if (isVenueEditor) {
        return;
      }
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    setVenueId(entity.id);
  }, [isVenueEditor, entity]);

  async function loadProductionCenters() {
    setLoading(true);

    try {
      const response = await getStockProductionCenters(venueId);
      setPaginationData(response);
    } catch {
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (!venueId) {
      setPaginationData(undefined);
      setLoading(false);
      return;
    }
    if (currentPageListNumber) {
      loadProductionCenters();
    }
  }, [currentPageListNumber, venueId]);

  const renderEditButton = (productionCenter: StockProductionCenter): JSX.Element => (
    <IconButton
      color="inherit"
      onClick={() => {
        setSelectedProductionCenter(productionCenter);
        history.push(`/private/stock/production-centers/${productionCenter.id}/edit`);
      }}
    >
      <Edit />
    </IconButton>
  );

  if (loading) {
    return <CircularProgress />;
  }

  const renderContent = (paginationData: StockProductionCenter[]): JSX.Element => {
    return (
      <>
        {paginationData.map((productionCenter) => (
          <TableRow key={productionCenter.id}>
            <TableCell>{productionCenter.name}</TableCell>
            <TableCell>{t(`stock.production-centers.types.${productionCenter.type}`)}</TableCell>
            <TableCell>
              <BooleanIcon value={productionCenter.isActive} />
            </TableCell>
            <TableCell>{renderEditButton(productionCenter)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      {!isVenueEditor && (
        <SelectField
          name="venueId"
          label={t('venue')}
          placeholder={t('select')}
          value={venueId}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => setVenueId(value as string)}
          touched={false}
          error={false}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={() => undefined}
          isDisabled={false}
        />
      )}
      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        onClick={() => history.push('/private/stock/production-centers/create')}
      >
        {t('stock.production-centers.create')}
      </Button>

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.production-centers.table.headers.name')}</TableCell>
              <TableCell>{t('stock.production-centers.table.headers.type')}</TableCell>
              <TableCell>{t('stock.production-centers.table.headers.active')}</TableCell>
              <TableCell width={80}>{t('stock.production-centers.table.headers.actions')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.production-centers.table.no-results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${isNaN(from) ? 0 : from}-${isNaN(to) ? 0 : to} ${t('pagination-of')} ${
              count !== -1 && !isNaN(count) ? count : '0'
            }`;
          }}
        />
      )}
    </>
  );
};
